// Copyright (c) 2022 by Move Closer

import { Authentication, AuthServiceType, EventbusType, IEventbus } from '@movecloser/front-core'
import { Component, Mixins, Watch } from 'vue-property-decorator'

import { CartIdentifier, CartItemData } from '../../../../contexts/checkout/contracts'
import { Inject, logger } from '../../../../support'

import { AuthMixin } from '../../../auth/shared/mixins/authMixin'
import { IAuthMixin, UserModel } from '../../../auth/shared/contracts'
import { IToastMixin, ToastMixin } from '../../../shared/mixins/toast.mixin'
import { CART_ID_KEY, CartAddedItemData } from '../../services/cart'

import { CartModel, CartMutationTypes } from '../../contracts'

import { BaseCartMixin, IBaseCart } from './base-cart.mixin'
import { CartAnalyticsMixin } from '../../../shared/mixins/cart-analytics.mixin'

import { RouteName as CheckoutRoutes } from '../../routes'

enum CartInitState {
  AuthWithCart,
  AuthNoCart,
  GuestWithCart,
  GuestNoCart
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
@Component<InitCartMixin>({
  name: 'InitCartMixin',
  mounted (): void {
    this.initCartOnMount()
  }
})
export class InitCartMixin extends Mixins<IBaseCart, CartAnalyticsMixin, IAuthMixin, IToastMixin>(BaseCartMixin, CartAnalyticsMixin, AuthMixin, ToastMixin) {
  @Inject(AuthServiceType)
  protected readonly authService!: Authentication<UserModel>

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  public customerCart: CartModel | null = null

  /**
   * Tries to init cart on mounted.
   */
  protected async initCartOnMount (): Promise<void> {
    // Retrieve form local storage cart identifier.
    const cartId: CartIdentifier | null = window.localStorage.getItem(CART_ID_KEY)

    if (!this.isWaitingForAuth && !this.isLoggedInUser) {
      this.$store.commit(CartMutationTypes.SetLoading, true)
      // If there is no user but there is cartId
      try {
        if (cartId) {
          const cart = await this.cartService.resolveCart(cartId, 5)
          this.$store.commit(CartMutationTypes.SetCartId, cart.id)
          this.$store.commit(CartMutationTypes.SetCart, cart)

          if (cartId !== cart.id) {
            localStorage.setItem(CART_ID_KEY, cart.id)
          }
        } else {
          // If there is no user and no cart Id
          await this.createCart()
        }
      } catch (e) {
        logger(e, 'warn')
        this.showToast((e as Error).message, 'danger')
      }

      this.$store.commit(CartMutationTypes.SetLoading, false)
    }
  }

  @Watch('isLoggedInUser')
  protected async initializeCart (isAuthenticated: boolean, oldValue: boolean): Promise<void> {
    if (
      this.$store.getters['checkout/registering'] ||
      typeof window === 'undefined' || isAuthenticated === oldValue
    ) {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const skus = urlParams.get('skus')
    const hasProductSkusInURL = !!skus?.length

    if ((!isAuthenticated && oldValue) || hasProductSkusInURL) {
      this.deleteCart()
    }

    // Retrieve form local storage cart identifier.
    const cartId: CartIdentifier | null = window.localStorage.getItem(CART_ID_KEY)
    const state: CartInitState = isAuthenticated
      ? (cartId ? CartInitState.AuthWithCart : CartInitState.AuthNoCart)
      : (cartId ? CartInitState.GuestWithCart : CartInitState.GuestNoCart)

    this.$store.commit(CartMutationTypes.SetLoading, true)

    try {
      switch (state) {
        case CartInitState.AuthWithCart: {
          this.isMergerPending(true)

          this.customerCart = await this.cartService.loadCustomerCart()
          this.customerCart = await this.cartService.loadCartByIdentifier(this.customerCart.id)
          /** Current cart */
          const isCurrentCartPresale = InitCartMixin.isCurrentCartPresale(this.cart)
          /** Cart that is saved in within customer */
          const isPreviousCartPresale = InitCartMixin.isCurrentCartPresale(this.customerCart)

          if (!this.customerCart) {
            this.isMergerPending(false)
            return
          }

          if (isCurrentCartPresale) {
            await this.handleRegularCartItemsRemoval()
          } else if (isPreviousCartPresale) {
            await this.handlePresaleItemRemoval(cartId as string)
          } else {
            await this.handleRegularCartMerge(cartId as string, isPreviousCartPresale)
          }

          this.$store.commit(CartMutationTypes.SetCartId, this.customerCart.id)
          this.$store.commit(CartMutationTypes.SetCart, this.customerCart)

          localStorage.setItem(CART_ID_KEY, this.customerCart.id)

          this.isMergerPending(false)
        }
          break
        case CartInitState.AuthNoCart: {
          if (hasProductSkusInURL) {
            this.customerCart = await this.cartService.loadCustomerCart()
            this.customerCart = await this.cartService.loadCartByIdentifier(this.customerCart.id)
            await this.handleTotalCartItemsRemoval()
          }
          await this.createCustomerCart()
          break
        }
        case CartInitState.GuestWithCart:
          await this.loadCart(cartId as string)
          break
        case CartInitState.GuestNoCart:
          await this.createCart()
      }
    } catch (e) {
      logger(e, 'warn')
      this.showToast((e as Error).message, 'danger')
      await this.createCart()
    }

    if (hasProductSkusInURL) {
      try {
        const updatedCart = await this.cartService.addManyToCart(this.cartId, this.parseSkuEntries(skus.split(',')))
        this.eventBus.emit('app:cart.view', this.getCartViewPayload(updatedCart))
        this.refreshCart(updatedCart)
      } catch (error) {
        logger(error, 'warn')
        this.showToast((error as Error).message, 'danger')
      }
    }

    this.$store.commit(CartMutationTypes.SetLoading, false)
  }

  /**
   * Composes a customerCart without any presale item, strict!
   * @protected
   */
  protected async handlePresaleItemRemoval (cartId: string): Promise<void> {
    const presaleItem = InitCartMixin.presaleCartItem(this.customerCart)

    if (presaleItem && cartId !== this.customerCart!.id) {
      this.customerCart = await this.cartService.mergerCarts(cartId as string, this.customerCart!.id)
      this.customerCart = await this.cartService.removeFromCart(this.customerCart.id, presaleItem.uid)
    }

    await this.handleCartRedirect()
  }

  /**
   * Composes a customerCart without aby regular items leaving only presale item, strict!
   * @protected
   */
  protected async handleRegularCartItemsRemoval (): Promise<void> {
    const presaleItem = InitCartMixin.presaleCartItem(this.cart)

    if (presaleItem?.product.variants) {
      this.customerCart = await this.cartService.addToCart(this.customerCart!.id, Object.values(presaleItem.product.variants)[0].sku)

      const regularItems = this.customerCart.items.filter((item) => !item.isPresale)
      const regularItemsToRemove = regularItems.map((item) => {
        return {
          quantity: -item.quantity,
          uid: item.uid
        }
      })

      if (regularItemsToRemove.length > 0) {
        this.customerCart = await this.cartService.updateCartItems(this.customerCart.id, regularItemsToRemove)
      }
    }

    await this.handleCartRedirect()
  }

  /**
   * Composes a customerCart with total items qty 0
   * @protected
   */
  protected async handleTotalCartItemsRemoval (): Promise<void> {
    if (!this.customerCart?.id) {
      return
    }

    const itemsToRemove = this.customerCart?.items.map((item) => {
      return {
        quantity: 0,
        uid: item.uid
      }
    }) ?? []

    if (itemsToRemove.length > 0) {
      this.customerCart = await this.cartService.updateCartItems(this.customerCart.id, itemsToRemove)
    }
  }

  /**
   * Handles regular carts merge
   * @protected
   */
  protected async handleRegularCartMerge (cartId: string, isPreviousCartPresale: boolean): Promise<void> {
    if (cartId !== this.customerCart!.id && !isPreviousCartPresale) {
      this.customerCart = await this.cartService.mergerCarts(cartId as string, this.customerCart!.id)

      // await this.handleCartRedirect()
    }
  }

  /**
   * Handles redirect when user has outOfStock cart items
   * @private
   */
  private async handleCartRedirect (): Promise<void> {
    await this.$router.push({ name: `checkout.${CheckoutRoutes.Cart}` })
  }

  /**
   * Checks whether current cart is presale
   * @private
   */
  private static isCurrentCartPresale (cart: CartModel | null): boolean {
    if (!cart) {
      return false
    }

    return cart.items.some((item) => item.isPresale)
  }

  /**
   * Finds presale item in cart items
   * @private
   */
  private static presaleCartItem (cart: CartModel | null): CartItemData | null | undefined {
    if (!cart) {
      return
    }

    return cart.items.find((item) => item.isPresale)
  }

  private isMergerPending (loading: boolean): void {
    this.$store.commit(CartMutationTypes.SetIsCartMergerPending, loading)
  }

  private parseSkuEntries (entries: string[]): CartAddedItemData[] {
    return entries.map(entry => {
      const [sku, quantityStr] = entry.split('*')
      return { sku, quantity: Number(quantityStr) }
    })
  }
}
